/* =====================================================================================
   Tab presence. Answers one question: is this same screen open in another tab or window?

   The screen that needed it is Circus's add-document dialog. Two tabs open on the same
   competitor's documents cannot see each other's uploads until one reloads, and that blind
   spot is exactly where duplicate documents come from: each tab honestly reports the file
   is not there yet. The dialog therefore warns when a sibling is open, rather than trying
   to synchronise two live lists, because a warning a person can act on is worth more than
   a reconciliation nobody can inspect.

   BroadcastChannel where it exists, which is every browser this application supports. Each
   subscriber announces itself ('hello'), siblings holding the same key answer ('here'),
   and departures say so ('bye', also fired on pagehide so closing the tab counts). A
   periodic sweep re-announces and rebuilds the peer set, so a tab that crashed without
   saying goodbye stops being counted within seconds rather than forever.

   A localStorage heartbeat is the fallback for anything without BroadcastChannel: each
   holder of the key writes a timestamp every two seconds under its own subkey, and a
   sibling is any other subkey whose timestamp is fresh. The 'storage' event makes the
   detection immediate rather than waiting for the next scan.
   ===================================================================================== */

import { useEffect, useState } from 'react';

const CHANNEL = 'forum.tab-presence';
const HEARTBEAT_MS = 2_000;
const SWEEP_MS = 5_000;
const STALE_MS = 6_000;

/* One identity per tab, minted once. Two components in the same tab holding the same key
   would otherwise report each other, and the dialog pattern only ever opens one at a time. */
const TAB_ID: string = (() => {
  try { return crypto.randomUUID(); }
  catch { return `${Date.now()}-${Math.random().toString(36).slice(2)}`; }
})();

interface Signal { type: 'hello' | 'here' | 'bye'; key: string; tab: string }

/**
 * True while any other tab or window holds the same key. Pass null to hold nothing,
 * which is how a closed dialog stops announcing without unmounting its parent.
 */
export function useTabPresence(key: string | null): boolean {
  const [others, setOthers] = useState(false);

  useEffect(() => {
    if (!key) { setOthers(false); return; }

    if (typeof BroadcastChannel !== 'undefined') {
      const ch = new BroadcastChannel(CHANNEL);
      const peers = new Set<string>();
      const post = (type: Signal['type']) => ch.postMessage({ type, key, tab: TAB_ID });

      ch.onmessage = (e: MessageEvent) => {
        const m = e.data as Signal;
        if (!m || m.key !== key || m.tab === TAB_ID) return;
        if (m.type === 'hello') { peers.add(m.tab); post('here'); }
        if (m.type === 'here') peers.add(m.tab);
        if (m.type === 'bye') peers.delete(m.tab);
        setOthers(peers.size > 0);
      };

      post('hello');

      /* Rebuild the peer set from scratch on a cadence. A crashed tab never says 'bye',
         and a warning that outlives its cause teaches people to ignore it. Live peers
         answer 'hello' within milliseconds; the verdict waits long enough for them. */
      const sweep = window.setInterval(() => {
        peers.clear();
        post('hello');
        window.setTimeout(() => setOthers(peers.size > 0), 800);
      }, SWEEP_MS);

      const bye = () => post('bye');
      window.addEventListener('pagehide', bye);
      return () => {
        bye();
        window.removeEventListener('pagehide', bye);
        window.clearInterval(sweep);
        ch.close();
      };
    }

    /* Fallback: heartbeat in localStorage. */
    const prefix = `forum.presence.${key}.`;
    const mine = prefix + TAB_ID;

    const scan = () => {
      const now = Date.now();
      let found = false;
      const dead: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (!k || !k.startsWith(prefix) || k === mine) continue;
        const at = Number(localStorage.getItem(k) ?? 0);
        if (now - at < STALE_MS) found = true; else dead.push(k);
      }
      dead.forEach((k) => localStorage.removeItem(k));
      setOthers(found);
    };

    const beat = () => { try { localStorage.setItem(mine, String(Date.now())); } catch { /* full */ } };
    beat(); scan();
    const timer = window.setInterval(() => { beat(); scan(); }, HEARTBEAT_MS);
    const onStorage = (e: StorageEvent) => { if (e.key?.startsWith(prefix)) scan(); };
    window.addEventListener('storage', onStorage);
    const drop = () => { try { localStorage.removeItem(mine); } catch { /* gone */ } };
    window.addEventListener('pagehide', drop);
    return () => {
      drop();
      window.removeEventListener('pagehide', drop);
      window.removeEventListener('storage', onStorage);
      window.clearInterval(timer);
    };
  }, [key]);

  return others;
}
